"use client";

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { formatCompactNumber } from "@/lib/format";
import type { NicheRanking } from "@/lib/types";

type ValidateIdeaResult = {
  niche: NicheRanking;
  matchType: "category" | "startup";
};

type CategoryOption = { slug: string; label: string };

type Props = {
  totalNiches: number;
  totalStartups: number;
  medianDemand: number;
  medianStartups: number;
  medianRevPerVisitor: number;
  medianGapScore: number;
  categories: CategoryOption[];
  suggestedTerms: string[];
  /** Pre-fill the search bar with this term (e.g. category label) so research uses it. */
  initialTerm?: string;
};
const PLACEHOLDER = "invoice SaaS, CRM tool, AI writing...";

type VerdictKey = "strong" | "hiddenGem" | "crowded" | "moderate" | "risky";

function getVerdict(
  niche: NicheRanking,
  medianDemand: number,
  medianStartups: number,
  medianGapScore: number
): { key: VerdictKey; label: string; sublabel: string } {
  const demand = niche.marketDemand ?? 0;
  const startups = niche.startupCount;
  const gapScore = niche.gapScore ?? 0;
  const score = niche.opportunityScore ?? 0;

  if (demand > 0 && demand < medianDemand * 0.5) {
    return {
      key: "risky",
      label: "Low Demand — Risky",
      sublabel: "Market demand is below average. Validate before building."
    };
  }
  if (score >= 65) {
    return {
      key: "strong",
      label: "Strong Opportunity",
      sublabel: "High demand with manageable competition"
    };
  }
  if (score >= 50) {
    return {
      key: "crowded",
      label: "Crowded but Profitable",
      sublabel: "Competitive market with proven monetization"
    };
  }
  if (
    gapScore > medianGapScore * 1.2 &&
    startups < medianStartups &&
    demand >= medianDemand
  ) {
    return {
      key: "hiddenGem",
      label: "Hidden Gem",
      sublabel: "Underserved niche with strong monetization potential"
    };
  }
  return {
    key: "moderate",
    label: "Moderate Opportunity",
    sublabel: "Mixed signals. Dig deeper before deciding."
  };
}

const VERDICT_STYLES: Record<
  VerdictKey,
  { bg: string; color: string; icon: string }
> = {
  strong: { bg: "var(--success-muted)", color: "var(--success)", icon: "🏆" },
  hiddenGem: { bg: "var(--accent-muted)", color: "var(--accent)", icon: "💎" },
  crowded: { bg: "var(--warning-muted)", color: "var(--warning)", icon: "⚔️" },
  moderate: {
    bg: "var(--bg-elevated)",
    color: "var(--text-secondary)",
    icon: "📊"
  },
  risky: { bg: "var(--danger-muted)", color: "var(--danger)", icon: "⚠️" }
};

function getRelativeLabel(
  value: number,
  median: number,
  type: "demand" | "competition" | "rev"
): string {
  if (median <= 0) return "";
  const ratio = value / median;
  if (type === "competition") {
    return ratio > 1.2 ? "High" : ratio < 0.8 ? "Low" : "Average";
  }
  if (type === "rev") {
    return ratio > 1.2 ? "Strong" : ratio < 0.8 ? "Weak" : "Average";
  }
  return ratio > 1.2 ? "High" : ratio < 0.8 ? "Low" : "Average";
}

export function IdeaValidator({
  totalNiches,
  totalStartups,
  medianDemand,
  medianStartups,
  medianRevPerVisitor,
  medianGapScore,
  categories,
  suggestedTerms,
  initialTerm = ""
}: Props) {
  const router = useRouter();
  const [query, setQuery] = useState(initialTerm);
  type StackItem = {
    query: string;
    results: ValidateIdeaResult[];
    matchType: "category" | "startup" | null;
  };
  const [resultsStack, setResultsStack] = useState<StackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (queryOverride?: string) => {
    const raw = queryOverride ?? query;
    const q = typeof raw === "string" ? raw.trim() : "";
    if (!q || q.length < 2) return;

    setLoading(true);
    setError(null);
    if (queryOverride !== undefined) setQuery(queryOverride);

    try {
      const res = await fetch(
        `/api/tools/validate-idea?q=${encodeURIComponent(q)}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Request failed: ${res.status}`);
      }
      const data = await res.json();
      const item: StackItem = {
        query: q,
        results: data.results ?? [],
        matchType: data.matchType ?? null
      };
      setResultsStack((prev) => [...prev, item]);
      setQuery("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") search();
  };

  return (
    <section className="card" style={{ marginTop: "2rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h2 className="card-title" style={{ marginBottom: "0.5rem" }}>
          Validate Your SaaS Idea
        </h2>
        <p className="card-desc" style={{ margin: 0 }}>
          See competition, demand, and monetization for niches related to your
          idea.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginBottom: "1.5rem"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            alignItems: "center"
          }}
        >
          <input
            type="text"
            className="form-input"
            placeholder={PLACEHOLDER}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: "1 1 200px", minWidth: 0 }}
            aria-label="What SaaS idea are you exploring?"
          />
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => search()}
            disabled={loading || !query.trim() || query.trim().length < 2}
          >
            {loading ? "Searching…" : "Go"}
          </button>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "0.8rem",
            color: "var(--text-muted)"
          }}
        >
          Try: {suggestedTerms.join(", ")}
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginTop: "0.5rem"
          }}
        >
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--text-muted)"
            }}
          >
            OR
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label
              htmlFor="idea-validator-category"
              style={{
                fontSize: "0.8rem",
                color: "var(--text-muted)"
              }}
            >
              Browse by category
            </label>
            <select
              id="idea-validator-category"
              className="filter-select"
              value=""
              onChange={(e) => {
                const opt = categories.find(
                  (c) => c.slug === e.target.value
                );
                if (opt) search(opt.label);
              }}
              style={{ minWidth: 180 }}
            >
              <option value="">Select niche</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: "0.75rem",
          color: "var(--text-muted)"
        }}
      >
        Based on {totalStartups.toLocaleString("en-US")} SaaS startups across{" "}
        {totalNiches} niches
      </p>

      {error && (
        <div
          className="error-card"
          style={{ marginTop: "1rem" }}
        >
          {error}
        </div>
      )}

      {loading && resultsStack.length === 0 && (
        <div
          style={{
            padding: "2.5rem",
            textAlign: "center",
            color: "var(--text-muted)",
            fontSize: "0.9rem"
          }}
        >
          Searching SaaS database…
        </div>
      )}

      {resultsStack.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          {resultsStack.map((item, stackIndex) => (
            <div
              key={`${item.query}-${stackIndex}`}
              style={{
                marginBottom:
                  stackIndex < resultsStack.length - 1 ? "2.5rem" : 0,
                paddingBottom:
                  stackIndex < resultsStack.length - 1 ? "2rem" : 0,
                borderBottom:
                  stackIndex < resultsStack.length - 1
                    ? "1px solid var(--border)"
                    : undefined
              }}
            >
              {item.results.length === 0 ? (
                <div
                  style={{
                    padding: "2.5rem 1.5rem",
                    textAlign: "center",
                    background: "var(--bg-elevated)",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border)"
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 0.5rem",
                      fontWeight: 600,
                      color: "var(--text-primary)"
                    }}
                  >
                    No niches matched &quot;{item.query}&quot;
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.9rem",
                      color: "var(--text-secondary)",
                      lineHeight: 1.6
                    }}
                  >
                    Try: broader terms, product category (CRM, analytics), or
                    customer type (HR, marketing)
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "1rem" }}>
                    <p
                      style={{
                        margin: "0 0 0.25rem",
                        fontWeight: 600,
                        color: "var(--text-primary)"
                      }}
                    >
                      Found {item.results.length} niche
                      {item.results.length !== 1 ? "s" : ""} related to &quot;
                      {item.query}&quot;
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.8rem",
                        color: "var(--text-muted)"
                      }}
                    >
                      Match type:{" "}
                      {item.matchType === "category"
                        ? "Direct category match"
                        : "Startup names containing your query"}
                    </p>
                    <p
                      style={{
                        margin: "0.5rem 0 0",
                        fontSize: "0.85rem",
                        color: "var(--text-secondary)",
                        lineHeight: 1.5
                      }}
                    >
                      Best match:{" "}
                      <strong>{item.results[0]!.niche.label}</strong> —{" "}
                      {getVerdict(
                        item.results[0]!.niche,
                        medianDemand,
                        medianStartups,
                        medianGapScore
                      ).sublabel}
                    </p>
                  </div>

                  <div className="idea-validator-grid">
                    {item.results.map((r, i) => {
                  const verdict = getVerdict(
                    r.niche,
                    medianDemand,
                    medianStartups,
                    medianGapScore
                  );
                  const style = VERDICT_STYLES[verdict.key];
                  const demandLabel = getRelativeLabel(
                    r.niche.marketDemand ?? 0,
                    medianDemand,
                    "demand"
                  );
                  const compLabel = getRelativeLabel(
                    r.niche.startupCount,
                    medianStartups,
                    "competition"
                  );
                  const revLabel = getRelativeLabel(
                    r.niche.avgRevenuePerVisitor ?? 0,
                    medianRevPerVisitor,
                    "rev"
                  );

                  return (
                    <div
                      key={r.niche.category}
                      className="card"
                      style={{
                        padding: "1.25rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0.5rem"
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: "var(--text-muted)"
                          }}
                        >
                          #{i + 1}
                        </span>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "var(--text-primary)"
                          }}
                        >
                          {r.niche.label}
                        </h3>
                      </div>

                      <div
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "var(--radius-sm)",
                          background: style.bg,
                          color: style.color,
                          fontWeight: 700,
                          fontSize: "0.85rem"
                        }}
                      >
                        {style.icon} {verdict.label}
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          color: "var(--text-muted)",
                          lineHeight: 1.4
                        }}
                      >
                        {verdict.sublabel}
                      </p>

                      <div
                        style={{
                          display: "grid",
                          gap: "0.5rem",
                          fontSize: "0.85rem"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between"
                          }}
                        >
                          <span style={{ color: "var(--text-muted)" }}>
                            Market
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between"
                          }}
                        >
                          <span style={{ color: "var(--text-secondary)" }}>
                            Demand
                          </span>
                          <span>
                            {formatCompactNumber(r.niche.marketDemand ?? 0)}
                            {demandLabel && (
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontSize: "0.75rem",
                                  color: "var(--text-muted)"
                                }}
                              >
                                ({demandLabel})
                              </span>
                            )}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between"
                          }}
                        >
                          <span style={{ color: "var(--text-secondary)" }}>
                            Competition
                          </span>
                          <span>
                            {r.niche.startupCount} startups
                            {compLabel && (
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontSize: "0.75rem",
                                  color: "var(--text-muted)"
                                }}
                              >
                                ({compLabel})
                              </span>
                            )}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 4
                          }}
                        >
                          <span style={{ color: "var(--text-muted)" }}>
                            Monetization
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between"
                          }}
                        >
                          <span style={{ color: "var(--text-secondary)" }}>
                            Rev / Visitor
                          </span>
                          <span>
                            $
                            {(r.niche.avgRevenuePerVisitor ?? 0).toFixed(2)}
                            {revLabel && (
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontSize: "0.75rem",
                                  color: "var(--text-muted)"
                                }}
                              >
                                ({revLabel})
                              </span>
                            )}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 4
                          }}
                        >
                          <span style={{ color: "var(--text-muted)" }}>
                            Score
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between"
                          }}
                        >
                          <span style={{ color: "var(--text-secondary)" }}>
                            Opportunity
                          </span>
                          <span style={{ fontWeight: 700 }}>
                            {r.niche.opportunityScore}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ marginTop: "auto", alignSelf: "flex-start" }}
                        onClick={() =>
                          router.push(`/dashboard/${r.niche.category}`)
                        }
                      >
                        View Niche →
                      </button>
                    </div>
                  );
                })}
                  </div>
                </>
              )}
            </div>
          ))}

          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid var(--border)"
            }}
          >
            <p
              style={{
                margin: "0 0 0.5rem",
                fontSize: "0.8rem",
                color: "var(--text-muted)"
              }}
            >
              Search another idea
            </p>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="text"
                className="form-input"
                placeholder={PLACEHOLDER}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ flex: "1 1 200px", minWidth: 0 }}
              />
              {loading && (
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "var(--text-muted)"
                  }}
                >
                  Searching…
                </span>
              )}
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => search()}
                disabled={loading || !query.trim() || query.trim().length < 2}
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
